// src/pages/Categories.js

import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Button, 
    Paper, 
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Divider,
    useTheme,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TablePagination,
    Menu,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    InputAdornment,
    Tooltip,
    Card,
    CardContent,
    CardActions,
    Tabs,
    Tab,
    Avatar,
    Alert,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { 
    Delete, 
    Category, 
    PictureAsPdf,
    TableChart, 
    GetApp,
    Print,
    Close as CloseIcon,
    Add as AddIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Today as TodayIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    CalendarMonth,
    ViewModule,
    ViewList,
    Inventory,
    Description,
    Info,
    LocalOffer,
    CheckCircle,
    Cancel,
    MoreVert
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APICategoriesDelete,
    APICategoriesList,
    APICategoriesStore,
    APICategoriesUpdate,
    APICategoriesExportExcel,
    APICategoriesExportPDF,
    APICategoriesPrint,
    APICategoriesStats
} from '../../apiServices/APIs';
import { formatCreatedAtDate, recordsCount, recordsPerPage, getStatusById, statusData } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorrState';
import SystemInformation from '../../components/SystemInformation';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Categories = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [selectedCategoryForAction, setSelectedCategoryForAction] = useState(null);

    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('list');
    const [statistics, setStatistics] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        status: '',
        description: ''
    });

    const [filters, setFilters] = useState({
        name: '',
        status: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        name: false,
        description: false,
    });

    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Use the updated permission hook
    const { hasPermission, loading: permissionLoading } = usePermission();

    useEffect(() => {
        apiService.setToastFunction(showToast);
        fetchData();
        fetchStatistics();
    }, [showToast]);

    // Fetch categories only (products_count comes from API)
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.list(userBranchId, APICategoriesList);

            if (result.success) {
                const categories = Array.isArray(result.data) ? result.data : [];

                setPageState(prev => ({
                    ...prev,
                    pageData: categories,
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch statistics
    const fetchStatistics = async () => {
        try {
            const userBranchId = getUserDetails("branchId");
            const result = await apiService.listStats(userBranchId, APICategoriesStats);
            if (result.success && result.data) {
                setStatistics(result.data);
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
        }
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handlePrint = async (record) => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to print category details", "error");
            return;
        }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('print_id', record.id);
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            const result = await apiService.print(APICategoriesPrint, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file generated successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to generate PDF file", "error");
            }
        } catch (error) {
            console.error("Print error:", error);
            showToast("Error", "Failed to generate PDF file", "error");
        }
    };

    const handleExportExcel = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export category data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing Excel export...", "info");
            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.status) formDataToSend.append('status', filters.status);
            if (filters.dateFrom) formDataToSend.append('date_from', filters.dateFrom);
            if (filters.dateTo) formDataToSend.append('date_to', filters.dateTo);
            
            const result = await apiService.exportToExcel(APICategoriesExportExcel, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Excel file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export Excel file", "error");
            }
        } catch (error) {
            console.error('Excel export error:', error);
            showToast("Error", error.message || "Failed to export Excel file", "error");
        }
    };

    const handleExportPDF = async () => {
        if (!hasPermission('EXPORT')) {
            showToast("Permission Denied", "You don't have permission to export category data", "error");
            handleExportClose();
            return;
        }

        try {
            handleExportClose();
            showToast("Processing", "Preparing PDF export...", "info");

            const formDataToSend = new FormData();
            formDataToSend.append('branch_id', getUserDetails("branchId"));
            formDataToSend.append('user_name', getUserDetails("userName"));
            formDataToSend.append('user_id', getUserDetails("Id"));
            
            if (filters.name) formDataToSend.append('name', filters.name);
            if (filters.status) formDataToSend.append('status', filters.status);
            
            const result = await apiService.exportToPDF(APICategoriesExportPDF, formDataToSend);

            if (result && result.success) {
                showToast("Success", "PDF file downloaded successfully", "success");
            } else {
                showToast("Error", result.message || "Failed to export PDF file", "error");
            }
        } catch (error) {
            console.error('PDF export error:', error);
            showToast("Error", error.message || "Failed to export PDF file", "error");
        }
    };

    // Filter based on search term and filters
    const filteredData = pageState.pageData.filter(category => {
        const matchesSearch = searchTerm === '' ||
            category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.description?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesName = !filters.name || 
            category.name?.toLowerCase().includes(filters.name.toLowerCase());

        const matchesStatus = !filters.status || 
            String(category.status) === String(filters.status);

        let matchesDate = true;
        if (filters.dateFrom) {
            const categoryDate = new Date(category.created_at).toISOString().split('T')[0];
            const filterFromDate = new Date(filters.dateFrom).toISOString().split('T')[0];
            matchesDate = matchesDate && categoryDate >= filterFromDate;
        }

        if (filters.dateTo) {
            const categoryDate = new Date(category.created_at).toISOString().split('T')[0];
            const filterToDate = new Date(filters.dateTo).toISOString().split('T')[0];
            matchesDate = matchesDate && categoryDate <= filterToDate;
        }

        return matchesSearch && matchesName && matchesStatus && matchesDate;
    });

    // Get total products for a category (from API data)
    const getTotalProducts = (id) => {
        const category = pageState.pageData.find(cat => cat.id === id);
        return category?.products_count || 0;
    };

    // Calculate filtered totals - History.js style
    const calculateFilteredTotals = () => {
        const activeCount = filteredData.filter(cat => cat.status == 1).length;
        const inactiveCount = filteredData.filter(cat => cat.status != 1).length;
        const totalProducts = filteredData.reduce((sum, cat) => sum + (cat.products_count || 0), 0);
        const avgProductsPerCategory = filteredData.length > 0 ? Math.round(totalProducts / filteredData.length) : 0;

        return {
            activeCount,
            inactiveCount,
            totalProducts,
            avgProductsPerCategory,
            totalCount: filteredData.length
        };
    };

    const filteredTotals = calculateFilteredTotals();

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Calculate totals from statistics
    const calculateTotals = () => {
        if (statistics) {
            return {
                total: statistics.total_categories || 0,
                active: statistics.active_categories || 0,
                inactive: statistics.inactive_categories || 0,
                totalProducts: statistics.total_products_in_categories || 0
            };
        }
        
        const active = pageState.pageData.filter(cat => cat.status == 1).length;
        const inactive = pageState.pageData.filter(cat => cat.status != 1).length;
        const totalProducts = pageState.pageData.reduce((sum, cat) => sum + (cat.products_count || 0), 0);
        
        return {
            total: pageState.pageData.length,
            active,
            inactive,
            totalProducts
        };
    };

    // Dialog handlers
    const handleFilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        if (!hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add categories", "error");
            return;
        }

        setPostStatus("ADD");
        setFormData({
            name: '',
            description: '',
            status: '',
        });
        setErrors({
            name: false,
            description: false,
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
        handleActionMenuClose();
    };

    const handleCloseView = () => {
        setOpenView(false);
        setActiveTab(0);
    };

    const handleOpenEdit = (record) => {
        if (!hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to edit categories", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");

        setFormData({
            name: record.name || '',
            description: record.description || '',
            status: String(record.status || ''),
        });
        setOpenAdd(true);
        handleActionMenuClose();
    };

    const handleOpenDelete = (record) => {
        if (!hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete categories", "error");
            return;
        }

        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
        handleActionMenuClose();
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
    };

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        setErrors(prev => ({
            ...prev,
            [name]: false
        }));
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Filter handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        handleCloseOptions();
    };

    const applyThisMonthFilter = () => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

        setFilters({
            ...filters,
            dateFrom: firstDay,
            dateTo: lastDay
        });
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            name: '',
            status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const applyFilters = () => {
        handleCloseOptions();
    };

    const validateForm = () => {
        if (!formData.name?.trim()) {
            showToast("Validation Error", "Category name is required", "warning");
            return false;
        }
        if (!formData.description?.trim()) {
            showToast("Validation Error", "Description is required", "warning");
            return false;
        }
        if (!String(formData.status || "").trim()) {
            showToast("Validation Error", "Status is required", "warning");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (postStatus === "ADD" && !hasPermission('ADD')) {
            showToast("Permission Denied", "You don't have permission to add categories", "error");
            return;
        }

        if (postStatus === "UPDATE" && !hasPermission('EDIT')) {
            showToast("Permission Denied", "You don't have permission to update categories", "error");
            return;
        }

        if (postStatus === "DELETE" && !hasPermission('DELETE')) {
            showToast("Permission Denied", "You don't have permission to delete categories", "error");
            return;
        }

        if (postStatus !== "DELETE") {
            if (!validateForm()) return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('branch_id', getUserDetails("branchId"));

        if (postStatus !== "DELETE") {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('status', formData.status);
            formDataToSend.append('description', formData.description);
        }

        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APICategoriesStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APICategoriesUpdate, formDataToSend);
            } else if (postStatus === "DELETE") {
                result = await apiService.delete(APICategoriesDelete, formDataToSend);
            }

            if (result && result.success) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                handleCloseDelete();
                fetchData();
                fetchStatistics();
            }
        } catch (error) {
            console.error("API Error:", error);
        }
    };

    // Action menu handlers
    const handleActionMenuOpen = (event, category) => {
        event.stopPropagation();
        setActionMenuAnchorEl(event.currentTarget);
        setSelectedCategoryForAction(category);
    };

    const handleActionMenuClose = () => {
        setActionMenuAnchorEl(null);
        setSelectedCategoryForAction(null);
    };

    // Render grid view
    const renderGridView = () => {
        if (viewMode !== 'grid') return null;

        const currentData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        if (currentData.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Category sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No categories found
                    </Typography>
                </Box>
            );
        }

        return (
            <Grid container spacing={2} sx={{ p: 2 }}>
                {currentData.map((record) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={record.id}>
                        <Card sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': { boxShadow: 6 }
                        }}>
                            <CardContent sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Avatar sx={{ 
                                        bgcolor: record.status == 1 ? theme.palette.success.main : theme.palette.error.main 
                                    }}>
                                        <LocalOffer />
                                    </Avatar>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            label={record.status == 1 ? 'Active' : 'Inactive'}
                                            color={record.status == 1 ? 'success' : 'error'}
                                            size="small"
                                        />
                                        <IconButton 
                                            size="small" 
                                            sx={{ color: theme.palette.action.main }}
                                            onClick={(e) => handleActionMenuOpen(e, record)}
                                            aria-label="actions"
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </Box>
                                </Box>
                                <Typography variant="h6" component="div" gutterBottom>
                                    {record.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {record.description}
                                </Typography>
                                <Chip
                                    icon={<Inventory />}
                                    label={`${getTotalProducts(record.id)} Products`}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const totals = calculateTotals();

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    if (loading) {
        return (
            <LoadingState
                message="Loading Categories Data..."
                variant="medical"
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchData}
                title="Unable to Load Categories Data"
                variant="medical"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Categories</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                    {totals.total}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 56, height: 56 }}>
                                <Category sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Active Categories</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                                    {totals.active}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.success.light, width: 56, height: 56 }}>
                                <CheckCircle sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Inactive Categories</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                                    {totals.inactive}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.error.light, width: 56, height: 56 }}>
                                <Cancel sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Total Products</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                                    {totals.totalProducts}
                                </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: theme.palette.secondary.light, width: 56, height: 56 }}>
                                <Inventory sx={{ fontSize: 32 }} />
                            </Avatar>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Search and Filter Bar */}
            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                        flexShrink: 0
                    }}>
                        Categories Management ({recordsCount(pageState.pageData.length)} Total)
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {hasPermission('ADD') && (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Add New Category
                            </Button>
                        )}

                        {hasPermission('VIEW') && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handleFilterOptions}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                View Options
                            </Button>
                        )}

                        {hasPermission('EXPORT') && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                                fetchData();
                                fetchStatistics();
                            }}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                        >
                            Refresh
                        </Button>

                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(e, val) => val && setViewMode(val)}
                            size="small"
                        >
                            <ToggleButton value="list">
                                <ViewList />
                            </ToggleButton>
                            <ToggleButton value="grid">
                                <ViewModule />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Paper sx={{
                        width: '300px',
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: 'auto',
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>

                {/* Active Filters */}
                {(filters.name || filters.status || filters.dateFrom || filters.dateTo) && (
                    <Box sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Active filters:
                        </Typography>
                        {filters.name && (
                            <Chip
                                label={`Name: ${filters.name}`}
                                onDelete={() => setFilters(prev => ({ ...prev, name: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.status && (
                            <Chip
                                label={`Status: ${filters.status == 1 ? 'Active' : 'Inactive'}`}
                                onDelete={() => setFilters(prev => ({ ...prev, status: '' }))}
                                color={filters.status == 1 ? 'success' : 'error'}
                                variant="outlined"
                            />
                        )}
                        {filters.dateFrom && (
                            <Chip
                                label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        {filters.dateTo && (
                            <Chip
                                label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                color="primary"
                                variant="outlined"
                            />
                        )}
                        <Button
                            size="small"
                            onClick={clearFilters}
                            startIcon={<CloseIcon />}
                            sx={{ ml: 1 }}
                        >
                            Clear All
                        </Button>
                    </Box>
                )}
            </Paper>

            {/* Export Menu */}
            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
                    <TableChart fontSize="small" color="success" />
                    Export to Excel (.xlsx)
                </MenuItem>
                <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
                    <PictureAsPdf fontSize="small" color="error" />
                    Export to PDF (.pdf)
                </MenuItem>
            </Menu>

            {/* Action Menu for List Items */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleActionMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {hasPermission('VIEW') && (
                    <MenuItem onClick={() => selectedCategoryForAction && handleOpenView(selectedCategoryForAction)}>
                        <ListItemIcon><ViewIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'primary.main' }}>View</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('PRINT') && (
                    <MenuItem onClick={() => selectedCategoryForAction && handlePrint(selectedCategoryForAction)}>
                        <ListItemIcon><Print fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText sx={{ color: 'warning.main' }}>Print</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('EDIT') && (
                    <MenuItem onClick={() => selectedCategoryForAction && handleOpenEdit(selectedCategoryForAction)}>
                        <ListItemIcon><EditIcon fontSize="small" color="secondary" /></ListItemIcon>
                        <ListItemText sx={{ color: 'secondary.main' }}>Edit</ListItemText>
                    </MenuItem>
                )}
                {hasPermission('DELETE') && (
                    <MenuItem onClick={() => selectedCategoryForAction && handleOpenDelete(selectedCategoryForAction)}>
                        <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                {/* List View */}
                {viewMode === 'list' && (
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Date Created</TableCell>
                                        <TableCell>Category Name</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="center">Total Products</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((category, index) => (
                                        <TableRow
                                            key={category.id || index}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CalendarMonth color="action" sx={{ mr: 1, fontSize: 16 }} />
                                                    {formatCreatedAtDate(category.created_at)}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                {category.name}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ 
                                                    maxWidth: '200px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {category.description}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {category.status == 1 ? (
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label="Active"
                                                        size="small"
                                                        color="success"
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label="Inactive"
                                                        size="small"
                                                        color="error"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    icon={<Inventory />}
                                                    label={getTotalProducts(category.id)}
                                                    size="small"
                                                    color="secondary"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Actions">
                                                    <IconButton 
                                                        sx={{ color: theme.palette.action.main }}
                                                        onClick={(e) => handleActionMenuOpen(e, category)}
                                                        size="small"
                                                    >
                                                        <MoreVert />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                
                                {/* Footer with Totals - History.js Style */}
                                {filteredData.length > 0 && (
                                    <TableBody>
                                        <TableRow sx={{
                                            backgroundColor: theme.palette.primary.light + '20',
                                            borderTop: '3px solid',
                                            borderTopColor: theme.palette.primary.main,
                                            '& td': {
                                                fontWeight: 'bold',
                                                py: 2.5
                                            }
                                        }}>
                                            <TableCell colSpan={2} align="right">
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                                                    Totals ({filteredTotals.totalCount} categories):
                                                </Typography>
                                            </TableCell>
                                            <TableCell></TableCell>
                                            <TableCell></TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Chip
                                                        icon={<CheckCircle />}
                                                        label={`${filteredTotals.activeCount} Active`}
                                                        size="small"
                                                        color="success"
                                                    />
                                                    <Chip
                                                        icon={<Cancel />}
                                                        label={`${filteredTotals.inactiveCount} Inactive`}
                                                        size="small"
                                                        color="error"
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                                    <Chip
                                                        icon={<Inventory />}
                                                        label={`${filteredTotals.totalProducts} Total`}
                                                        size="small"
                                                        color="secondary"
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg: {filteredTotals.avgProductsPerCategory} per category
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="caption" color="text.secondary">
                                                    —
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />
                    </Paper>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && renderGridView()}

                {filteredData.length === 0 && viewMode === 'list' && (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 8,
                        color: 'text.secondary'
                    }}>
                        <Category sx={{ fontSize: 60, mb: 2 }} />
                        <Typography variant="h6">No categories found</Typography>
                        <Typography variant="body2">Try adjusting your search query or add a new category</Typography>
                    </Box>
                )}
            </motion.div>

            {/* Filter Options Dialog */}
            <Dialog
                open={filterOptions}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseOptions();
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={clearFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            Show All Categories
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<TodayIcon />}
                            onClick={applyTodayFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show Today's Categories
                        </Button>

                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            onClick={applyThisMonthFilter}
                            sx={{ textTransform: 'none' }}
                        >
                            Show This Month's Categories
                        </Button>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Fields
                        </Typography>

                        <TextField
                            fullWidth
                            label="Category Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                        />

                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                label="Status"
                            >
                                <MenuItem value="">All Status</MenuItem>
                                {statusData.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider>OR</Divider>

                        <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                            Filter by Date Range
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="From Date"
                                    type="date"
                                    name="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="To Date"
                                    type="date"
                                    name="dateTo"
                                    value={filters.dateTo}
                                    onChange={handleFilterChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseOptions} variant="contained">
                        Close
                    </Button>
                    <Button onClick={applyFilters} color="primary" variant="contained">
                        Apply Filters
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog
                open={openAdd}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAdd();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                scroll="paper"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>{postStatus === "ADD" ? "Add New Category" : "Edit Category"}</b>
                </DialogTitle>
                <DialogContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                        <Box>
                            <Grid item xs={12}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                                    Category Information
                                </Typography>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Category Name *"
                                    name="name"
                                    value={formData.name}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocalOffer />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Status *</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        autoComplete="off"
                                        onChange={handleInputChange}
                                        label="Status *"
                                        required
                                    >
                                        {statusData.map((item, index) => (
                                            <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <br />
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Description *"
                                    name="description"
                                    value={formData.description}
                                    autoComplete="off"
                                    onChange={handleInputChange}
                                    required
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Description />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Box>
                        <DialogActions sx={{ mt: 3, px: 0 }}>
                            <Button
                                color="error"
                                onClick={handleCloseAdd}
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                {postStatus === "ADD" ? "Add Category" : "Update Category"}
                            </Button>
                        </DialogActions>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* View Dialog */}
            <Dialog
                open={openView}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseView();
                    }
                }}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ textAlign: 'center' }}>
                    <b>Category Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box>
                            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Details" icon={<Info />} iconPosition="start" />
                                <Tab label="Products" icon={<Inventory />} iconPosition="start" />
                            </Tabs>

                            {activeTab === 0 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                            <Avatar sx={{ 
                                                bgcolor: selectedRecord.status == 1 ? theme.palette.success.main : theme.palette.error.main,
                                                width: 64,
                                                height: 64
                                            }}>
                                                <LocalOffer sx={{ fontSize: 36 }} />
                                            </Avatar>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="h6" align="center" color="primary">
                                            {selectedRecord.name}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Status:</b>{' '}
                                            {selectedRecord.status == 1 ? (
                                                <Chip
                                                    icon={<CheckCircle />}
                                                    label="Active"
                                                    size="small"
                                                    color="success"
                                                    sx={{ ml: 1 }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<Cancel />}
                                                    label="Inactive"
                                                    size="small"
                                                    color="error"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Description:</b> {selectedRecord.description}
                                        </Typography>
                                    </Grid>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Box sx={{ p: 2 }}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">
                                            <b>Total Products:</b>{' '}
                                            <Chip
                                                icon={<Inventory />}
                                                label={getTotalProducts(selectedRecord.id)}
                                                size="small"
                                                color="secondary"
                                                sx={{ ml: 1 }}
                                            />
                                        </Typography>
                                    </Grid>
                                    <br />
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Products associated with this category
                                        </Typography>
                                    </Grid>
                                </Box>
                            )}

                              {/* System Information - Card Style */}
                                <SystemInformation
                                    createdBy={selectedRecord.created_by}
                                    updatedBy={selectedRecord.updated_by}
                                    createdAt={selectedRecord.created_at}
                                    updatedAt={selectedRecord.updated_at}
                                    title="Category System Information"
                                />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseView} variant="contained">
                        Close
                    </Button>
                    {hasPermission('PRINT') && selectedRecord && (
                        <Button onClick={() => handlePrint(selectedRecord)} color="info" variant="contained">
                            <Print sx={{ mr: 1 }} /> Print
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={openDelete}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDelete();
                    }
                }}
                TransitionComponent={Transition}
            >
                <DialogTitle sx={{ textAlign: 'center' }}>Delete Category</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                        Are you sure you want to delete this category?
                    </Typography>
                    {selectedRecord && (
                        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                            {selectedRecord.name}
                        </Typography>
                    )}
                    {selectedRecord && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Chip
                                icon={<Inventory />}
                                label={`${getTotalProducts(selectedRecord.id)} products will be affected`}
                                color="warning"
                                variant="outlined"
                            />
                        </Box>
                    )}
                    <Typography variant="body2" color="error" sx={{ textAlign: 'center', mt: 2 }}>
                        Warning: This action cannot be undone!
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button color="error" onClick={handleCloseDelete} variant="contained">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary" variant="contained">
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Categories;